import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User } from 'lucide-react';
import Navigation from '../components/Navigation';
import apiClient from '../utils/api';
import { handleApiError } from '../utils/errorHandler';
import wsClient from '../utils/websocket';
import { useTranslation } from '../i18n';
import { trackChatMessage, trackFeatureUsage } from '../utils/analytics';
import { getUserPermissions } from '../utils/auth';
import { fetchConfig } from '../config';

// Add CSS for streaming cursor animation, thinking dots, and markdown spacing
const streamingStyles = `
  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
  
  @keyframes bounce {
    0%, 80%, 100% {
      transform: scale(0);
    }
    40% {
      transform: scale(1);
    }
  }
  
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.8;
    }
  }
  
  /* Aggressive markdown spacing reset */
  .chat-markdown * {
    margin: 0 !important;
    padding: 0 !important;
  }
  
  .chat-markdown p {
    display: inline !important;
    margin: 0 !important;
    line-height: 1.4;
  }
  
  .chat-markdown p + p {
    display: block !important;
    margin-top: 0.5rem !important;
  }
  
  .chat-markdown ul, .chat-markdown ol {
    display: block !important;
    margin: 0.5rem 0 !important;
    padding-left: 1.5rem !important;
  }
  
  .chat-markdown li {
    display: list-item !important;
    margin: 0.125rem 0 !important;
  }
  
  .chat-markdown strong {
    font-weight: bold !important;
  }
  
  .chat-markdown em {
    font-style: italic !important;
  }
  
  .chat-markdown code {
    background-color: #f1f1f1 !important;
    padding: 0.125rem 0.25rem !important;
    border-radius: 0.25rem !important;
    font-family: monospace !important;
  }
  
  .chat-markdown a {
    color: #007bff !important;
    text-decoration: underline !important;
  }
  
  .chat-markdown a:hover {
    color: #0056b3 !important;
  }
  
  .chat-markdown br {
    line-height: 0.5 !important;
  }
`;

// Inject styles
if (!document.getElementById('streaming-styles')) {
  const style = document.createElement('style');
  style.id = 'streaming-styles';
  style.textContent = streamingStyles;
  document.head.appendChild(style);
}

const Chat = () => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [activeTools, setActiveTools] = useState(new Set());
  const [selectedAgent, setSelectedAgent] = useState('gtd');
  const toolUseCountRef = useRef(0);
  const responseStartTimeRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const reconnectNotificationShown = useRef(false);
  
  // Get user permissions
  const userPermissions = getUserPermissions();
  const isPowerUser = userPermissions.role === 'power_user' || userPermissions.role === 'admin';

  // Simple markdown parser for basic formatting
  const parseSimpleMarkdown = (text) => {
    const parts = [];
    let lastIndex = 0;
    
    // Match **bold** text
    const boldRegex = /\*\*(.*?)\*\*/g;
    let match;
    
    while ((match = boldRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      // Add bold text
      parts.push(<strong key={match.index}>{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    
    return parts.length > 0 ? parts : [text];
  };

  // Global error handler for displaying errors
  const showError = (errorMessage) => {
    const errorMsg = {
      id: Date.now(),
      type: 'error',
      content: errorMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, errorMsg]);
    setIsLoading(false);
    setActiveTools(new Set());
  };

  // Success message handler
  const showSuccess = (successMessage) => {
    const successMsg = {
      id: Date.now(),
      type: 'success',
      content: successMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, successMsg]);
  };

  // Remove message by ID
  const removeMessage = (messageId) => {
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    console.log('Chat component mounted');
    fetchRecentMessages();
    
    // Register WebSocket message handler
    wsClient.onMessage((data) => {
      console.log('Received WebSocket data:', data);
      
      if (data.type === 'chunk') {
        setMessages(prev => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg?.type === 'agent' && lastMsg?.streaming) {
            return prev.slice(0, -1).concat({
              ...lastMsg,
              content: lastMsg.content + data.content
            });
          }
          return prev;
        });
      } else if (data.type === 'tool_use') {
        console.log('Tool use detected, count:', toolUseCountRef.current + 1);
        setActiveTools(prev => new Set([...prev, Date.now()]));
        toolUseCountRef.current += 1;
      } else if (data.type === 'tool_result') {
        setActiveTools(prev => {
          const newSet = new Set(prev);
          const firstItem = newSet.values().next().value;
          if (firstItem) newSet.delete(firstItem);
          return newSet;
        });
      } else if (data.type === 'done') {
        const responseTime = responseStartTimeRef.current ? Date.now() - responseStartTimeRef.current : null;
        const toolCount = toolUseCountRef.current;
        console.log('Response done - tools:', toolCount, 'time:', responseTime);
        
        setMessages(prev => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg?.type === 'agent') {
            return prev.slice(0, -1).concat({
              ...lastMsg,
              streaming: false,
              toolUseCount: toolCount,
              responseTime: responseTime
            });
          }
          return prev;
        });
        setIsLoading(false);
        setActiveTools(new Set());
        toolUseCountRef.current = 0;
        responseStartTimeRef.current = null;
      } else if (data.type === 'error') {
        const errorMessage = data.message || 'An error occurred';
        
        // For subscription and usage limit errors, don't show reconnection message
        if (errorMessage === 'SUBSCRIPTION_REQUIRED') {
          reconnectNotificationShown.current = true; // Prevent reconnection message
          showError(t('subscription.subscriptionRequired'));
        } else if (errorMessage === 'USAGE_LIMIT_EXCEEDED') {
          reconnectNotificationShown.current = true; // Prevent reconnection message
          showError(t('subscription.usageLimitExceeded'));
        } else {
          showError(errorMessage);
        }
        
        setIsLoading(false);
        setActiveTools(new Set());
        toolUseCountRef.current = 0;
        responseStartTimeRef.current = null;
      }
    });
    
    // Listen to connection status changes
    let disconnectMessageId = null;
    wsClient.onConnectionChange((connected) => {
      setWsConnected(connected);
      if (!connected && !reconnectNotificationShown.current) {
        reconnectNotificationShown.current = true;
        const errorMsg = {
          id: Date.now(),
          type: 'error',
          content: t('connectionLost'),
          timestamp: new Date()
        };
        disconnectMessageId = errorMsg.id;
        setMessages(prev => [...prev, errorMsg]);
      } else if (connected && reconnectNotificationShown.current) {
        // Remove disconnect message
        if (disconnectMessageId) {
          setMessages(prev => prev.filter(msg => msg.id !== disconnectMessageId));
        }
        // Show reconnection success
        showSuccess(t('connectionRestored'));
        reconnectNotificationShown.current = false;
      }
    });
    
    // Connect WebSocket with fresh token
    const connectWebSocket = async () => {
      try {
        // Get config to access clientId
        const config = await fetchConfig();
        const keyPrefix = `CognitoIdentityServiceProvider.${config.cognito.clientId}`;
        const lastAuthUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
        
        if (!lastAuthUser) {
          console.error('No authenticated user found');
          return;
        }
        
        const token = localStorage.getItem(`${keyPrefix}.${lastAuthUser}.idToken`);
        console.log('Token from Cognito storage:', token ? 'exists' : 'missing');
        
        if (token) {
          console.log('Attempting WebSocket connection...');
          await wsClient.connect(token);
          console.log('WebSocket ready');
          setWsConnected(true);
        } else {
          console.error('No token found in Cognito storage');
        }
      } catch (err) {
        console.error('WebSocket connection failed:', err);
        showError('Failed to connect to chat service');
      }
    };
    
    connectWebSocket();
    
    return () => {
      console.log('Chat component unmounting');
      wsClient.disconnect();
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
    // Refocus textarea after messages update (unless loading)
    if (!isLoading && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [messages, isLoading]);

  const fetchRecentMessages = async () => {
    try {
      const response = await apiClient.get('/conversations?limit=3');
      const conversations = response.data || [];
      
      const historyMessages = conversations.flatMap(conv => [
        {
          id: `${conv.message_id}_user`,
          type: 'user',
          content: conv.message,
          timestamp: new Date(conv.timestamp)
        },
        {
          id: conv.message_id,
          type: 'agent',
          content: conv.response,
          timestamp: new Date(conv.timestamp)
        }
      ]);
      
      setMessages(historyMessages);
    } catch (error) {
      console.error('Failed to load recent messages:', error);
      // Only show error if it's not a simple 404 (no conversations yet)
      if (error.response?.status !== 404) {
        showError(handleApiError(error, 'Failed to load conversation history. You can still send new messages.', t));
      }
    }
  };

  const sendMessage = () => {
    if (!inputMessage.trim() || isLoading || !wsConnected) return;

    const userMessage = inputMessage.trim();
    trackChatMessage(userMessage.length);
    trackFeatureUsage('chat_message');
    
    setInputMessage('');
    setTimeout(() => textareaRef.current?.focus(), 0);
    
    const newUserMessage = {
      id: Date.now(),
      type: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);
    responseStartTimeRef.current = Date.now();
    toolUseCountRef.current = 0;

    const agentMessage = {
      id: Date.now() + 1,
      type: 'agent',
      content: '',
      timestamp: new Date(),
      streaming: true
    };
    
    setMessages(prev => [...prev, agentMessage]);
    wsClient.sendMessage(userMessage, { agent: selectedAgent });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="chat-page-container">
      <Navigation />
      
      <div className="container" style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#f8f9fa'
      }}>
        {/* Header */}
        <div style={{ 
          padding: '1rem',
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ flex: 1 }}>
              <h1 style={{ fontSize: '1.125rem', fontWeight: '700', margin: 0, marginBottom: '0.25rem' }}>
                {t('chatWithAI')}
              </h1>
              <p style={{ fontSize: '0.875rem', color: '#666', margin: 0 }}>
                {t('chatSubtitle')}
              </p>
            </div>
            <div 
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: wsConnected ? '#4caf50' : '#f44336',
                animation: wsConnected ? 'pulse 2s infinite' : 'none',
                flexShrink: 0,
                cursor: 'help'
              }}
              title={wsConnected 
                ? t('connected') 
                : t('disconnectedReconnecting', { 
                    current: wsClient.reconnectAttempts, 
                    max: wsClient.maxReconnectAttempts 
                  })
              }
            />
          </div>
        </div>

        {/* Messages Area */}
        <div style={{ 
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          padding: '1rem'
        }}>
          {messages.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '2rem', 
              color: '#666',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <Bot size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                Welcome to SmartGTD Chat!
              </p>
              <p style={{ fontSize: '0.875rem' }}>
                Start a conversation by typing a message below
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {messages.map((message) => (
                <div
                  key={message.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    flexDirection: message.type === 'user' ? 'row-reverse' : 'row'
                  }}
                >
                  {/* Avatar */}
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: message.type === 'user' ? '#007bff' : message.type === 'error' ? '#dc3545' : message.type === 'success' ? '#28a745' : '#28a745',
                    color: 'white',
                    flexShrink: 0
                  }}>
                    {message.type === 'user' ? (
                      <User size={16} />
                    ) : message.type === 'error' ? (
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>⚠️</span>
                    ) : message.type === 'success' ? (
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                    ) : (
                      <Bot size={16} />
                    )}
                  </div>

                  {/* Message Bubble */}
                  <div style={{
                    maxWidth: '70%',
                    padding: '0.75rem 1rem',
                    borderRadius: message.type === 'user' ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
                    backgroundColor: message.type === 'user' ? '#007bff' : message.type === 'error' ? '#f8d7da' : message.type === 'success' ? '#d4edda' : 'white',
                    color: message.type === 'user' ? 'white' : message.type === 'error' ? '#721c24' : message.type === 'success' ? '#155724' : '#333',
                    border: message.type === 'user' ? 'none' : message.type === 'error' ? '1px solid #f5c6cb' : message.type === 'success' ? '1px solid #c3e6cb' : '1px solid #e0e0e0',
                    fontSize: '0.875rem',
                    lineHeight: '1.4',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    position: 'relative'
                  }}>
                    {message.type === 'agent' ? (
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {message.content.split(/<thinking>|<\/thinking>/).map((part, index) => {
                          if (index % 2 === 1) {
                            // This is thinking content
                            return (
                              <div key={index} style={{
                                backgroundColor: '#f0f8ff',
                                border: '1px solid #b3d9ff',
                                borderRadius: '0.5rem',
                                padding: '0.75rem',
                                margin: '0.5rem 0',
                                fontSize: '0.8rem',
                                color: '#0066cc',
                                fontStyle: 'italic',
                                whiteSpace: 'pre-wrap'
                              }}>
                                <div style={{
                                  fontSize: '0.7rem',
                                  fontWeight: 'bold',
                                  marginBottom: '0.25rem',
                                  color: '#004499',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  💭 {t('thinking')}
                                </div>
                                {part}
                              </div>
                            );
                          } else {
                            // This is regular content with simple markdown parsing
                            return part.trim() ? (
                              <span key={index}>{parseSimpleMarkdown(part)}</span>
                            ) : null;
                          }
                        })}
                        {/* Tool count and response time */}
                        {!message.streaming && (message.toolUseCount > 0 || message.responseTime) && (
                          <div style={{
                            fontSize: '0.7rem',
                            color: '#999',
                            marginTop: '0.5rem',
                            paddingTop: '0.5rem',
                            borderTop: '1px solid #f0f0f0',
                            display: 'flex',
                            gap: '1rem'
                          }}>
                            {message.toolUseCount > 0 && (
                              <span>🔧 {message.toolUseCount} {message.toolUseCount === 1 ? 'tool' : 'tools'}</span>
                            )}
                            {message.responseTime && (
                              <span>⚡ {(message.responseTime / 1000).toFixed(2)}s</span>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      message.content
                    )}
                    {message.streaming && (
                      <span style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '16px',
                        backgroundColor: '#007bff',
                        marginLeft: '2px',
                        animation: 'blink 1s infinite'
                      }} />
                    )}
                  </div>
                </div>
              ))}
              

              
              {/* Thinking indicator */}
              {isLoading && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#28a745',
                    color: 'white',
                    animation: 'pulse 2s ease-in-out infinite'
                  }}>
                    <Bot size={16} />
                  </div>
                  <div style={{
                    padding: '0.75rem 1rem',
                    borderRadius: '1rem 1rem 1rem 0.25rem',
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <div style={{
                      display: 'flex',
                      gap: '4px'
                    }}>
                      {[0, 1, 2].map((i) => (
                        <div
                          key={i}
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: '#666',
                            animation: `bounce 1.4s ease-in-out ${i * 0.16}s infinite both`
                          }}
                        />
                      ))}
                    </div>
                    <span style={{ fontSize: '0.875rem', color: '#666', marginLeft: '0.5rem' }}>
                      {activeTools.size > 0 ? t('usingTools', { count: toolUseCountRef.current }) : t('thinking')}
                    </span>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div style={{
          padding: '1rem',
          borderTop: '1px solid #e0e0e0',
          backgroundColor: 'white',
          flexShrink: 0
        }}>
          {/* Agent Selector for Power Users */}
          {isPowerUser && (
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ 
                fontSize: '0.75rem', 
                color: '#666', 
                marginBottom: '0.25rem',
                display: 'block'
              }}>
                Agent:
              </label>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="orchestrator">🎯 Orchestrator (Auto-route)</option>
                <option value="gtd">✅ GTD Agent (Tasks & Calendar)</option>
                <option value="financial">💰 Financial Agent (Money & Budget)</option>
              </select>
            </div>
          )}
          <div style={{ 
            display: 'flex', 
            gap: '0.75rem',
            alignItems: 'flex-end'
          }}>
            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('typeMessage')}
              disabled={isLoading}
              style={{
                flex: 1,
                minHeight: '44px',
                maxHeight: '120px',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '0.75rem',
                fontSize: '0.875rem',
                resize: 'none',
                outline: 'none',
                fontFamily: 'inherit',
                backgroundColor: 'white',
                boxSizing: 'border-box'
              }}
              rows={1}
            />
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: inputMessage.trim() && !isLoading ? '#007bff' : '#e0e0e0',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: inputMessage.trim() && !isLoading ? 'pointer' : 'not-allowed',
                transition: 'background-color 0.2s'
              }}
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;